import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { LOCKUP, MARK } from './brandPaths'

type Art = { readonly viewBox: string; readonly ink: string; readonly red: string }

interface BrandProps {
  /** Sizes it; set a height (`h-7 w-auto`) or a square (`size-7`). */
  className?: string
  /**
   * The ink's fill, a theme token: the page's foreground by default, the
   * sidebar's on the sidebar (some themes draw it dark on a light page).
   */
  ink?: string
  /** Names it for assistive tech ("VentaWiz"); decorative without. */
  label?: boolean
}

/**
 * The VentaWiz brand, drawn from traced vector paths (`brandPaths.ts`) in two
 * colours that are both theme tokens: the ink follows the text it sits in,
 * dark on light themes and light on dark ones, and the red is `--logo`.
 */
function Brand({
  art,
  className,
  ink = 'fill-foreground',
  label = false,
}: BrandProps & { art: Art }) {
  const { t } = useTranslation()
  const named = label ? { role: 'img', 'aria-label': t('common.appName') } : { 'aria-hidden': true }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={art.viewBox}
      focusable="false"
      className={cn('shrink-0', className)}
      {...named}
    >
      <path className={ink} d={art.ink} />
      <path className="fill-logo" d={art.red} />
    </svg>
  )
}

/** The striped monogram alone: square, for an icon rail or a tile. */
export function BrandMark(props: BrandProps) {
  return <Brand art={MARK} {...props} />
}

/** The mark and "Ventawiz" together, spaced as designed. */
export function BrandLogo(props: BrandProps) {
  return <Brand art={LOCKUP} {...props} />
}
